import type { TSkillNotificationType } from "@frontend-shared/types";
import {
  ENotificationType,
  ESkillNotificationTone,
} from "@frontend-shared/types";
import {
  formatSkillEditedFields,
  resolveSkillNotificationView,
} from "@frontend-shared/utils";
import { PencilLine, Puzzle, ShieldHalf, UserMinus } from "lucide-react";
import { memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { NotificationCardShell } from "../NotificationCardShell";
import type { ISkillNotificationCardProps } from "./types";

const ICON_CLASS_BY_TONE: Record<ESkillNotificationTone, string> = {
  [ESkillNotificationTone.ACCENT]: "h-5 w-5 text-amber-500",
  [ESkillNotificationTone.DESTRUCTIVE]: "h-5 w-5 text-destructive",
};

const ICON_BG_CLASS_BY_TONE: Record<ESkillNotificationTone, string> = {
  [ESkillNotificationTone.ACCENT]: "bg-amber-500/10",
  [ESkillNotificationTone.DESTRUCTIVE]: "bg-destructive/10",
};

const ICON_BY_TYPE: Record<TSkillNotificationType, typeof Puzzle> = {
  [ENotificationType.SKILL_CREATED]: Puzzle,
  [ENotificationType.SKILL_SHARED]: Puzzle,
  [ENotificationType.SKILL_PERMISSION_CHANGED]: ShieldHalf,
  [ENotificationType.SKILL_ACCESS_REVOKED]: UserMinus,
  [ENotificationType.SKILL_EDITED]: PencilLine,
};

// NOTE: NSAI-6952 — one card for all five skill notification types. What it
// says and whether it offers "View skill" comes from
// resolveSkillNotificationView in frontend-shared, which the mobile card reads
// too; this file only owns the web icon and layout.
const SkillNotificationCardComponent = ({
  uuid,
  notification,
  createdAt,
  isRead,
  onViewSkill,
}: ISkillNotificationCardProps): React.JSX.Element => {
  const { t } = useTranslation("notifications");
  const view = useMemo(
    () => resolveSkillNotificationView(notification),
    [notification],
  );
  const Icon = ICON_BY_TYPE[notification.type];

  const body = useMemo(
    (): string =>
      t(view.bodyKey, {
        ...view.bodyParams,
        changedFields: formatSkillEditedFields({
          changedFields: view.changedFields,
          translateLabel: (labelKey) => t(labelKey),
        }),
      }),
    [t, view],
  );

  const handleViewSkill = useCallback(
    (): void =>
      onViewSkill({ skillUuid: view.skillUuid, projectUuid: view.projectUuid }),
    [onViewSkill, view.skillUuid, view.projectUuid],
  );

  return (
    <NotificationCardShell
      uuid={uuid}
      variantTestId={notification.type}
      createdAt={createdAt}
      isRead={isRead}
      icon={<Icon className={ICON_CLASS_BY_TONE[view.tone]} />}
      iconBgClassName={ICON_BG_CLASS_BY_TONE[view.tone]}
      ctaLabel={view.hasViewSkillCta ? t("view_skill") : undefined}
      onCtaClick={view.hasViewSkillCta ? handleViewSkill : undefined}
      body={body}
    />
  );
};

export const SkillNotificationCard = memo(SkillNotificationCardComponent);
